import { spacing, layout } from '../../../tokens/tokens-base/properties/build/js/index.js';
import { buildSpacingVariants, buildSpacingInnerVariants } from '../../../_common/utils/tokensVariants.utils.js';
import '../../../_common/utils/buildAllBreakpointsWithGivenBreakpoints.js';
import '../../../_common/utils/console.js';
import 'react';
const SPACING_TOKENS = Object.keys(buildSpacingVariants());
const SPACING_INNER_TOKENS = Object.keys(buildSpacingInnerVariants());
const {
  xs,
  sm,
  md,
  lg,
  xl,
  xxl
} = layout;
const generateGapVariantsWithoutBreakpoints = cssProp => {
  return SPACING_TOKENS.reduce((variantsValues, spacingToken) => {
    return Object.assign(Object.assign({}, variantsValues), {
      [spacingToken]: {
        [cssProp]: spacing.layout[spacingToken]
      }
    });
  }, {});
};
const generateGapInnerVariantsWithoutBreakpoints = cssProp => {
  return SPACING_INNER_TOKENS.reduce((variantsValues, spacingInnerToken) => {
    const capitalizeSpacingToken = spacingInnerToken.charAt(0).toUpperCase() + spacingInnerToken.slice(1);
    return Object.assign(Object.assign({}, variantsValues), {
      [`inner${capitalizeSpacingToken}`]: {
        [cssProp]: spacing.inner[spacingInnerToken]
      }
    });
  }, {});
};
const generateGapVariantsWithBreakpoints = (breakpoint, cssProp) => {
  return SPACING_TOKENS.reduce((variantsValues, spacingToken) => {
    const mediaQuery = breakpoint === 'xxl' ? `@${breakpoint}Min` : `@${breakpoint}MinMax`;
    return Object.assign(Object.assign({}, variantsValues), {
      [spacingToken]: {
        [mediaQuery]: {
          [cssProp]: spacing.layout[spacingToken]
        }
      }
    });
  }, {});
};
const generateGapInnerVariantsWithBreakpoints = (breakpoint, cssProp) => {
  return SPACING_INNER_TOKENS.reduce((variantsValues, spacingToken) => {
    const mediaQuery = breakpoint === 'xxl' ? `@${breakpoint}Min` : `@${breakpoint}MinMax`;
    const capitalizeSpacingToken = spacingToken.charAt(0).toUpperCase() + spacingToken.slice(1);
    return Object.assign(Object.assign({}, variantsValues), {
      [`inner${capitalizeSpacingToken}`]: {
        [mediaQuery]: {
          [cssProp]: spacing.layout[spacingToken]
        }
      }
    });
  }, {});
};
const getGenericGap = () => Object.assign({
  // gap: 'none'
  none: {},
  // gap: 'responsive'
  responsive: {
    '@xsMinMax': {
      gap: xs.gutters
    },
    '@smMinMax': {
      gap: sm.gutters
    },
    '@mdMinMax': {
      gap: md.gutters
    },
    '@lgMinMax': {
      gap: lg.gutters
    },
    '@xlMinMax': {
      gap: xl.gutters
    },
    '@xxlMin': {
      gap: xxl.gutters
    }
  }
}, generateGapVariantsWithoutBreakpoints('gap'));
const getGenericGapInner = () => Object.assign({
  // gap: 'none'
  none: {},
  // gap: 'responsive'
  responsive: {
    '@xsMinMax': {
      gap: xs.gutters
    },
    '@smMinMax': {
      gap: sm.gutters
    },
    '@mdMinMax': {
      gap: md.gutters
    },
    '@lgMinMax': {
      gap: lg.gutters
    },
    '@xlMinMax': {
      gap: xl.gutters
    },
    '@xxlMin': {
      gap: xxl.gutters
    }
  }
}, generateGapInnerVariantsWithoutBreakpoints('gap'));
// gap: { vertical?: SpacingTokens; horizontal?: SpacingTokens; }
const getVerticalAndHorizontalGaps = () => ({
  verticalGap: generateGapVariantsWithoutBreakpoints('rowGap'),
  horizontalGap: generateGapVariantsWithoutBreakpoints('columnGap')
});
// gap: { vertical?: SpacingInnerToken; horizontal?: SpacingInnerToken; }
const getVerticalAndHorizontalGapsInner = () => ({
  verticalGap: generateGapInnerVariantsWithoutBreakpoints('rowGap'),
  horizontalGap: generateGapInnerVariantsWithoutBreakpoints('columnGap')
});
// gap: { vertical?: GapVariant & GapInnerVariant; horizontal?: GapVariant & GapInnerVariant; }
const mergeVerticalAndHorizontalGapsWithInner = () => {
  const verticalAndHorizontalGaps = getVerticalAndHorizontalGaps();
  const verticalAndHorizontalGapsInner = getVerticalAndHorizontalGapsInner();
  return {
    horizontalGap: Object.assign(Object.assign({}, verticalAndHorizontalGaps.horizontalGap), verticalAndHorizontalGapsInner.horizontalGap),
    verticalGap: Object.assign(Object.assign({}, verticalAndHorizontalGaps.verticalGap), verticalAndHorizontalGapsInner.verticalGap)
  };
};
// gap: { xs?: SpacingTokens, sm?: SpacingTokens, md?: SpacingTokens, (...) }
const getGapsVariantsWithAllBreakpoints = () => ({
  xsGap: generateGapVariantsWithBreakpoints('xs', 'gap'),
  smGap: generateGapVariantsWithBreakpoints('sm', 'gap'),
  mdGap: generateGapVariantsWithBreakpoints('md', 'gap'),
  lgGap: generateGapVariantsWithBreakpoints('lg', 'gap'),
  xlGap: generateGapVariantsWithBreakpoints('xl', 'gap'),
  xxlGap: generateGapVariantsWithBreakpoints('xxl', 'gap')
});
// gap: { xs?: SpacingInnerToken, sm?: SpacingInnerToken, md?: SpacingInnerToken, (...) }
const getGapsInnerVariantsWithAllBreakpoints = () => ({
  xsGap: generateGapInnerVariantsWithBreakpoints('xs', 'gap'),
  smGap: generateGapInnerVariantsWithBreakpoints('sm', 'gap'),
  mdGap: generateGapInnerVariantsWithBreakpoints('md', 'gap'),
  lgGap: generateGapInnerVariantsWithBreakpoints('lg', 'gap'),
  xlGap: generateGapInnerVariantsWithBreakpoints('xl', 'gap'),
  xxlGap: generateGapInnerVariantsWithBreakpoints('xxl', 'gap')
});
// gap: { xs?: SpacingInnerToken & GapVariant, sm?: SpacingInnerToken & GapVariant, md?: SpacingInnerToken & GapVariant, (...) }
const mergeGapsInnerAndGapsVariantsWithAllBreakpoints = () => {
  const gapsVariantsWithAllBreakpoints = getGapsVariantsWithAllBreakpoints();
  const gapsInnerVariantsWithAllBreakpoints = getGapsInnerVariantsWithAllBreakpoints();
  return {
    xsGap: Object.assign(Object.assign({}, gapsVariantsWithAllBreakpoints.xsGap), gapsInnerVariantsWithAllBreakpoints.xsGap),
    smGap: Object.assign(Object.assign({}, gapsVariantsWithAllBreakpoints.smGap), gapsInnerVariantsWithAllBreakpoints.smGap),
    mdGap: Object.assign(Object.assign({}, gapsVariantsWithAllBreakpoints.mdGap), gapsInnerVariantsWithAllBreakpoints.mdGap),
    lgGap: Object.assign(Object.assign({}, gapsVariantsWithAllBreakpoints.lgGap), gapsInnerVariantsWithAllBreakpoints.lgGap),
    xlGap: Object.assign(Object.assign({}, gapsVariantsWithAllBreakpoints.xlGap), gapsInnerVariantsWithAllBreakpoints.xlGap),
    xxlGap: Object.assign(Object.assign({}, gapsVariantsWithAllBreakpoints.xxlGap), gapsInnerVariantsWithAllBreakpoints.xxlGap)
  };
};
export { generateGapInnerVariantsWithBreakpoints, generateGapInnerVariantsWithoutBreakpoints, generateGapVariantsWithBreakpoints, generateGapVariantsWithoutBreakpoints, getGapsInnerVariantsWithAllBreakpoints, getGapsVariantsWithAllBreakpoints, getGenericGap, getGenericGapInner, getVerticalAndHorizontalGaps, getVerticalAndHorizontalGapsInner, mergeGapsInnerAndGapsVariantsWithAllBreakpoints, mergeVerticalAndHorizontalGapsWithInner };